import { cqAPI, getBid } from "../../lib/Auth";
import {
  Switch,
  Text,
  Box,
  Flex,
  useColorMode,
  useToast,
  Button,
  Modal,
  ModalOverlay,
  useColorModeValue,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Radio,
  RadioGroup,
  VStack,
  Select as ChakraSelect,
  Spinner,
} from "@chakra-ui/react";
import Select from "react-select";
import React, { useEffect, useState, useRef } from "react";
import { AddIcon, DeleteIcon, CheckIcon } from "@chakra-ui/icons";
import EditableText from "../EditableText/EditableText";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import _ from "lodash";

const GenericSettings = ({
  configuration,
  onDeleteField,
  onAddInput,
  onChange,
}) => {
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const invalidInputRef = useRef(null);
  const toast = useToast();
  const { colorMode } = useColorMode();

  const handleRadioChange = (id, value) => {
    setSettings((prevSettings) => {
      const newSettings = {
        ...prevSettings,
        [id]: value,
      };
      onChange(newSettings);
      return newSettings;
    });
  };

  const handleSwitchChange = (id, event) => {
    const isChecked = event.target.checked;
    setSettings((prevSettings) => {
      const newSettings = {
        ...prevSettings,
        [id]: isChecked ? "" : undefined,
      };
      onChange(newSettings);
      return newSettings;
    });
  };

  const handleInputChange = (id, event) => {
    setSettings((prevSettings) => {
      const newSettings = {
        ...prevSettings,
        [id]: event.target.value,
      };
      onChange(newSettings);
      return newSettings;
    });
  };

  const handleSelectChange = (id, selectedOptions, isMulti) => {
    setSettings((prevSettings) => {
      const newSettings = {
        ...prevSettings,
        [id]: isMulti
          ? selectedOptions
            ? selectedOptions.map((option) => option.value)
            : []
          : selectedOptions.value,
      };
      onChange(newSettings);
      return newSettings;
    });
  };

  useEffect(() => {
    const changesDetected = !_.isEqual(settings, originalSettings);
    setHasChanges(changesDetected);
  }, [settings, originalSettings]);

  const bgColor = useColorModeValue("gray.100", "gray.700");
  const hintColor = useColorModeValue("gray.500", "gray.400");

  return (
    <Box p={4} w="100%" align="left">
      {configuration.map((section, index) => (
        <Box key={index} mb={4} p={4} bgColor={bgColor} borderRadius="md">
          <Text mb={4}>{section.title}</Text>
          <Text mb={4}>{section.description}</Text>
          {section.inputs.map((input, idx) => (
            <Box key={idx} mb={4}>
              <Text mb={1}>{input.name}</Text>
              {input.hint && (
                <Text fontSize="sm" mb={2} color={hintColor}>
                  {input.hint}
                </Text>
              )}
              {input.type === "radio" && (
                <RadioGroup
                  isDisabled={true}
                  onChange={(value) => handleRadioChange(input.id, value)}
                  value={settings[input.id] || ""}
                >
                  <VStack align="start">
                    {input.options.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </VStack>
                </RadioGroup>
              )}
              {input.type === "switch" && (
                <Flex align="center">
                  <Switch
                    isChecked={settings[input.id] !== undefined}
                    onChange={(event) => handleSwitchChange(input.id, event)}
                    isDisabled={true}
                  >
                    {input.name}
                  </Switch>
                  <IconButton
                    aria-label="Delete field"
                    icon={<DeleteIcon />}
                    ml={2}
                    onClick={() => onDeleteField(input.id)}
                  />
                </Flex>
              )}
              {input.type === "switchwt" && (
                <>
                  <Flex align="center">
                    <Switch
                      isChecked={settings[input.id] !== undefined}
                      onChange={(event) => handleSwitchChange(input.id, event)}
                      isDisabled={true}
                    >
                      {input.name}
                    </Switch>
                    <IconButton
                      aria-label="Delete field"
                      icon={<DeleteIcon />}
                      ml={2}
                      onClick={() => onDeleteField(input.id)}
                    />
                  </Flex>
                  {settings[input.id] !== undefined && (
                    <Input
                      mt={2}
                      value={settings[input.id] || ""}
                      onChange={(event) =>
                        handleSwitchChange(input.id, event)
                      }
                      placeholder={`Enter ${input.name} details`}
                    />
                  )}
                </>
              )}
              {input.type === "input" && (
                <Flex align="center">
                  <Input
                    id={input.id}
                    value={settings[input.id] || ""}
                    onChange={(event) => handleInputChange(input.id, event)}
                    placeholder={input.name}
                    isReadOnly={true}
                  />
                  <IconButton
                    aria-label="Delete field"
                    icon={<DeleteIcon />}
                    ml={2}
                    onClick={() => onDeleteField(input.id)}
                  />
                </Flex>
              )}
              {input.type === "select" && (
                <Flex align="center">
                  <Select
                    styles={{
                      control: (base) => ({
                        ...base,
                        background:
                          colorMode === "light" ? "#F7FAFC" : "#2D3748",
                        borderColor:
                          colorMode === "light" ? "#CBD5E0" : "#4A5568",
                        "&:hover": {
                          borderColor:
                            colorMode === "light" ? "#A0AEC0" : "#718096",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "#fff",
                      }),
                      multiValue: (base) => ({
                        ...base,
                        color: "#fff",
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor:
                          colorMode === "light" ? "#717274" : "white",
                        boxShadow: "0px 10px 20px rgba(0,0,0,0.2)",
                        border: "1px solid",
                        borderColor:
                          colorMode === "light" ? "#717274" : "#718096",
                        borderRadius: "4px",
                        zIndex: 9999,
                      }),
                      option: (base, state) => ({
                        ...base,
                        color: colorMode === "light" ? "white" : "black",
                        backgroundColor:
                          colorMode === "light" ? "#717274" : "white",
                      }),
                    }}
                    id={input.id}
                    value={input.options.filter((option) =>
                      settings[input.id] && Array.isArray(settings[input.id])
                        ? settings[input.id].includes(option.value)
                        : settings[input.id] === option.value
                    )}
                    onChange={(selectedOptions) =>
                      handleSelectChange(
                        input.id,
                        selectedOptions,
                        input.isMulti
                      )
                    }
                    options={input.options}
                    isMulti={input.isMulti}
                    isDisabled={true}
                  />
                  <IconButton
                    aria-label="Delete field"
                    icon={<DeleteIcon />}
                    ml={2}
                    onClick={() => onDeleteField(input.id)}
                  />
                </Flex>
              )}
            </Box>
          ))}
          <Button mt={4} onClick={() => onAddInput(index)}>
            Add Input
          </Button>
        </Box>
      ))}
    </Box>
  );
};

function History() {
  const convertToGeneric = (template) => {
    return template?.map((section) => {
      return {
        title: section.title,
        description: section.description || "",
        inputs: section.questions.map((q) => ({
          type:
            q.type === "t"
              ? "input"
              : q.type === "yn"
              ? "switch"
              : q.type === "multiple"
              ? "select"
              : q.type === "ynwt"
              ? "switchwt"
              : q.type,
          options: q?.options?.length ? q?.options : [],
          id: q.id,
          hint: q.hint,
          name: q.name || q.text,
        })),
      };
    });
  };
  const bgColor = useColorModeValue("gray.100", "gray.700");

  const toast = useToast();
  const bid = getBid();
  const [template, setTemplate] = useState({ data: [] });
  const [originalTemplate, setOriginalTemplate] = useState({ data: [] });
  const { colorMode } = useColorMode();
  const navigate = useNavigate();
  const { template_id } = useParams();
  const [isServerResponding, setIsServerResponding] = useState(false);

  const fetchHistoryTemplate = async () => {
    if (template_id === "new") {
      const newTemplate = { name: "Medical and Dental History", data: [] };
      setTemplate(newTemplate);
      setOriginalTemplate(_.cloneDeep(newTemplate));
      return;
    }
    const response = await cqAPI.get(
      "/settings/history?template_id=" + template_id
    );
    setTemplate(response.data);
    setOriginalTemplate(_.cloneDeep(response.data));
  };

  useEffect(() => {
    fetchHistoryTemplate();
  }, []);

  const updateTemplate = async () => {
    setIsServerResponding(true);
    const response = await cqAPI.put(`/settings/history`, {
      field: "data",
      value: template.data,
      template_id: template._id,
    });
    if (!response) {
      setIsServerResponding(false);
      return false;
    }
    if (response.type === "error") {
      if (response.msg) {
        toast({
          title: "Error updating the template.",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
      setIsServerResponding(false);
      return false;
    }
    await cqAPI.put(`/settings/history`, {
      field: "name",
      value: template.name,
      template_id: template._id,
    });
    setOriginalTemplate(_.cloneDeep(template));
    setIsServerResponding(false);
    return true;
  };

  const createTemplate = async () => {
    setIsServerResponding(true);
    const response = await cqAPI.post("/settings/history", {
      name: template.name,
      data: template.data,
    });
    if (response.type === "error") {
      if (response.msg) {
        toast({
          title: "Error creating the template.",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
      setIsServerResponding(false);
      return false;
    }
    setOriginalTemplate(_.cloneDeep(template));
    setIsServerResponding(false);
    return true;
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [newSectionDescription, setNewSectionDescription] = useState("");
  const [selectedSection, setSelectedSection] = useState(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSection(null);
  };

  const handleTitleChange = (e) => setNewSectionTitle(e.target.value);
  const handleDescriptionChange = (e) =>
    setNewSectionDescription(e.target.value);

  const addSection = () => {
    if (!newSectionTitle.trim()) {
      toast({
        title: "Title is required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const newSection = {
      title: newSectionTitle,
      description: newSectionDescription,
      questions: [],
    };
    setTemplate({ ...template, data: template.data.concat(newSection) });
    closeModal();
  };

  const [deleteField, setDeleteField] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  const handleDeleteClick = (field) => {
    setDeleteField(field);
    onOpen();
  };

  const confirmDelete = () => {
    if (deleteField) {
      const updatedTemplate = { ...template };
      updatedTemplate.data.forEach((section) => {
        section.questions = section.questions.filter(
          (q) => q.id !== deleteField
        );
      });
      setTemplate(updatedTemplate);
    }
    onClose();
  };

  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [inputType, setInputType] = useState("t");
  const [inputName, setInputName] = useState("");
  const [inputHint, setInputHint] = useState("");
  const [inputOptions, setInputOptions] = useState("");

  const openInputModal = (sectionIndex) => {
    setSelectedSection(sectionIndex);
    setInputModalOpen(true);
  };

  const closeInputModal = () => {
    setInputModalOpen(false);
    setInputType("t");
    setInputName("");
    setInputHint("");
    setInputOptions("");
  };

  const handleInputTypeChange = (e) => setInputType(e.target.value);
  const handleInputNameChange = (e) => setInputName(e.target.value);
  const handleInputHintChange = (e) => setInputHint(e.target.value);
  const handleInputOptionsChange = (e) => setInputOptions(e.target.value);

  const addInputToSection = () => {
    if (!inputName.trim() || !inputType.trim()) {
      toast({
        title: "Input name and type are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    console.log('Input name', inputName);

    const newInput = {
      type: inputType,
      name: inputName,
      hint: inputHint,
      options:
        inputType === "select" || inputType === "radio"
          ? inputOptions
              .split(",")
              .map((option) => ({ label: option.trim(), value: option.trim() }))
          : undefined,
      id: `${selectedSection}-${inputName
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase()}`,
    };

    //checking if this template section already has an input with same id
    if (
      template.data[selectedSection].questions.some(
        (question) => question.id === newInput.id
      )
    ) {
      toast({
        title: "An input with this name already exists in the section.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const updatedTemplate = { ...template };
    updatedTemplate.data[selectedSection].questions.push(newInput);
    setTemplate(updatedTemplate);
    closeInputModal();
  };

  const hasChanges = !_.isEqual(template, originalTemplate);

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
      position="relative"
    >
      <Box p={4} w="100%" align="left">
        <Box mb={4} p={4} bgColor={bgColor} borderRadius="md">
          <Flex justifyContent="space-between" alignItems="center">
            <Flex w="250px">
              <EditableText
                onSave={(newValue) => {
                  setTemplate({ ...template, name: newValue });
                  return true;
                }}
                initialValue={template.name}
                placeholder="Name"
                pattern={/[^\ ]{3,}/}
                invalid="more than 3 character."
              />
            </Flex>
            <Flex>
              {template_id !== "new" && (
                <Button
                  leftIcon={
                    isServerResponding ? <Spinner size="sm" /> : <CheckIcon />
                  }
                  onClick={updateTemplate}
                  marginRight="10px"
                  isDisabled={!hasChanges || isServerResponding}
                >
                  Update
                </Button>
              )}
              <Button
                leftIcon={
                  isServerResponding ? <Spinner size="sm" /> : <CheckIcon />
                }
                onClick={createTemplate}
                marginRight="10px"
                isDisabled={!hasChanges || isServerResponding}
              >
                Save as New
              </Button>
              <Button leftIcon={<AddIcon />} onClick={openModal}>
                Add Section
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Box>

      <GenericSettings
        configuration={convertToGeneric(template.data)}
        onDeleteField={handleDeleteClick}
        onAddInput={openInputModal}
        onChange={(newSettings) =>
          setTemplate({ ...template, data: newSettings })
        }
      />

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Section</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input value={newSectionTitle} onChange={handleTitleChange} />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Description</FormLabel>
              <Input
                value={newSectionDescription}
                onChange={handleDescriptionChange}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={addSection}>
              Add
            </Button>
            <Button variant="ghost" onClick={closeModal}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Field
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this field? This action cannot be
              undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal isOpen={inputModalOpen} onClose={closeInputModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Input</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Type</FormLabel>
              <ChakraSelect value={inputType} onChange={handleInputTypeChange}>
                <option value="input">Input</option>
                <option value="switch">Switch</option>
                <option value="switchwt">Switch with Text</option>
                <option value="select">Select</option>
                <option value="radio">Radio</option>
              </ChakraSelect>
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel>Name</FormLabel>
              <Input value={inputName} onChange={handleInputNameChange} />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Hint</FormLabel>
              <Input value={inputHint} onChange={handleInputHintChange} />
            </FormControl>
            {(inputType === "select" || inputType === "radio") && (
              <FormControl mt={4}>
                <FormLabel>Options (comma separated)</FormLabel>
                <Input
                  value={inputOptions}
                  onChange={handleInputOptionsChange}
                />
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={addInputToSection}>
              Add
            </Button>
            <Button variant="ghost" onClick={closeInputModal}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default History;
